import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SearchBar from "../components/SearchBar";
import CompanyTable from "../components/CompanyTable";
import SectorAverages from "../components/SectorAverages";
import { CAT_COLOR } from "../components/RiskBadge";
import WaitlistForm from "../components/WaitlistForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldCheck } from "lucide-react";
import { fetchStats, fetchLeaderboard, fetchSectors } from "../lib/api";

const LEAD_DIMS = [
  { v: "customer", label: "Customer", desc: "customer" },
  { v: "geographic", label: "Geography", desc: "geographic" },
  { v: "supplier", label: "Supplier", desc: "supplier" },
  { v: "product", label: "Product", desc: "product" },
  { v: "credit", label: "Credit", desc: "credit" },
];

const TRY = ["QCOM", "SWKS", "ANET", "NVDA", "F"];
const PILLS = [
  ["customer", "Customer"],
  ["supplier", "Supplier"],
  ["geographic", "Geographic"],
  ["product", "Product"],
  ["credit", "Credit"],
];

function StatCard({ value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-[#e8e6df] bg-[#f5f4ef] px-6 py-5 text-center">
      <div className="font-heading text-3xl font-bold text-[#1a1a18]">{value}</div>
      <div className="mt-1 text-sm text-neutral-500">{label}</div>
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [leadDim, setLeadDim] = useState("customer");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetchSectors().then(setSectors).catch(() => {});
  }, []);

  useEffect(() => {
    fetchLeaderboard(25, leadDim).then(setRows).catch(() => {});
  }, [leadDim]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center animate-fade-up">
        <p className="mt-6 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold leading-[1.15] tracking-tight text-[#1a1a18]">
          Small disclosures.
        </p>
        <h1 className="mt-2 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight" style={{ color: "#E24B4A" }}>
          Big exposure<span className="text-[#1a1a18]">.</span>
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base md:text-lg text-neutral-600 leading-relaxed">
          Concentration risk, disclosed in SEC filings—<br className="hidden sm:block" />
          structured, searchable, and tracked over time.
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {PILLS.map(([k, t]) => (
            <span key={k} className="inline-flex items-center gap-2 rounded-full border border-[#e8e6df] px-4 py-1.5 text-sm text-neutral-700">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CAT_COLOR[k] }} />
              {t}
            </span>
          ))}
        </div>

        <div className="mx-auto mt-8 max-w-2xl">
          <SearchBar large />
          <div className="mt-3 text-sm text-neutral-500">
            Try:{" "}
            {TRY.map((t, i) => (
              <span key={t}>
                <button onClick={() => navigate(`/company/${t}`)} data-testid={`try-${t}`}
                        className="font-mono underline-offset-2 hover:underline text-neutral-700">{t}</button>
                {i < TRY.length - 1 && <span className="mx-1.5 text-neutral-300">·</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      {stats && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testid="stat-companies" value={stats.companies.toLocaleString()} label="S&P 500 companies" />
            <StatCard testid="stat-dimensions" value={stats.risk_dimensions} label="Risk dimensions" />
            <StatCard testid="stat-disclosures" value={stats.disclosures_parsed.toLocaleString()} label="Disclosures parsed" />
            <StatCard testid="stat-history" value={`${stats.filing_history_years} yrs`} label="History" />
          </div>
          <div data-testid="trust-strip" className="mt-4 flex flex-col items-center justify-center gap-x-6 gap-y-1.5 rounded-lg border border-[#1D9E7540] bg-[#1D9E7508] px-6 py-4 text-sm text-neutral-600 sm:flex-row">
            <span className="inline-flex items-center gap-2 font-semibold text-[#157a59]">
              <ShieldCheck className="h-4 w-4" />
              Parsed straight from SEC EDGAR filings
            </span>
            <span className="hidden text-neutral-300 sm:inline">·</span>
            <span data-testid="trust-strip-note" className="font-medium text-[#157a59]">
              Figures link back to their SEC source documents.
            </span>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1a1a18]">Most concentrated S&amp;P 500 companies by</h2>
              <Select value={leadDim} onValueChange={setLeadDim}>
                <SelectTrigger data-testid="leaderboard-dimension" className="w-[170px] bg-white text-lg font-semibold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_DIMS.map((d) => (
                    <SelectItem key={d.v} value={d.v} data-testid={`leaderboard-opt-${d.v}`}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-neutral-500 mt-1">
              Top 25 ranked by the single largest {LEAD_DIMS.find((d) => d.v === leadDim)?.desc} concentration disclosed in each company's latest SEC filing. See all 500 in the screener.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/screener" data-testid="view-screener-link" className="text-sm font-medium text-neutral-700 hover:text-[#1a1a18]">View full screener →</Link>
          </div>
        </div>
        <CompanyTable rows={rows} />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <SectorAverages sectors={sectors} />
      </section>

      <section id="waitlist" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="rounded-lg border border-[#e8e6df] bg-[#f5f4ef] px-6 py-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1a1a18]">Full S&amp;P 500 access with a free account.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-neutral-600">
            Coming next: coverage beyond the S&amp;P 500, real-time filing alerts and CSV/API exports.
            Leave your email and we'll notify you the moment it's ready.
          </p>
          <div className="mt-6 flex justify-center">
            <WaitlistForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
